import React, { createContext, useCallback, useContext, useEffect, useMemo } from 'react'
import usePolicyTemplateGroup from '../hooks/usePolicyTemplateGroup'
import usePolicyTemplate from '../hooks/usePolicyTemplates'

export const POLICY_TEMPLATE_TYPES = {
  CONDITIONAL_ACCESS: 'CA',
  DEFENDER: 'DEFENDER',
  INTUNE_DEVICE_CONFIG: 'INTUNE_DEVICE_CONFIG',
  INTUNE_DEVICE_COMPLIANCE: 'INTUNE_DEVICE_COMPLIANCE',
  INTUNE_DEPLOYMENT_PROFILE: 'INTUNE_DEPLOYMENT_PROFILE',
  GROUPS: 'GROUPS',
  INTUNE_APP_PROTECTION: 'INTUNE_APP_PROTECTION',
  INTUNE_APP_CONFIGURATION: 'INTUNE_APP_CONFIGURATION',
  INTUNE_MOBILE_APPS: 'INTUNE_MOBILE_APPS',
  INTUNE_SCRIPTS: 'INTUNE_SCRIPTS',
  INTUNE_REMEDIATION_SCRIPTS: 'INTUNE_REMEDIATION_SCRIPTS',
  PURVIEW_DLP: 'PURVIEW_DLP',
  PURVIEW_RETENTION: 'PURVIEW_RETENTION',
  PURVIEW_SENSITIVITY_LABELS: 'PURVIEW_SENSITIVITY_LABELS',
  TEAMS_CHANNELS_POLICY: 'TEAMS_CHANNELS_POLICY',
  TEAMS_MEETING_POLICY: 'TEAMS_MEETING_POLICY',
  TEAMS_MESSAGING_POLICY: 'TEAMS_MESSAGING_POLICY',
  TEAMS_ORG_WIDE_SETTINGS: 'TEAMS_ORG_WIDE_SETTINGS',
}

export const PolicyTemplatesContext = createContext({})

export const usePolicyTemplatesContext = () => {
  const provided = useContext(PolicyTemplatesContext)

  return provided
}

/**
 * Provider for the policy templates and groups api
 */
export function PolicyTemplatesProvider(props) {
  const { children, fetchOnMount, policyType } = props // eslint-disable-line react/prop-types

  const {
    loading: loadingPolicyTemplates,
    data: policyTemplates,
    error: policyTemplatesError,
    requestPolicyTemplatesList,
    deletePolicyTemplate,
    createPolicyTemplate,
    updatePolicyTemplate,
    clearPolicyTemplateError,
  } = usePolicyTemplate()

  const {
    loading: loadingPolicyTemplateGroup,
    error: errorPolicyTemplateGroup,
    data: policyTemplateGroups,
    createPolicyTemplateGroup,
    fetchPolicyTemplateGroupList,
    deletePolicyTemplateGroup,
    updatePolicyTemplateGroup,
    clearPolicyTemplateGroupError,
  } = usePolicyTemplateGroup()

  const requestProviderPolicyTemplatesList = useCallback(
    ({ policyType: requestedPolicyType } = {}) =>
      requestPolicyTemplatesList({
        policyType: Array.isArray(policyType) ? policyType : (requestedPolicyType ?? policyType),
      }),
    [policyType, requestPolicyTemplatesList],
  )
  const fetchProviderPolicyTemplateGroupList = useCallback(
    ({ policyType: requestedPolicyType } = {}) =>
      fetchPolicyTemplateGroupList({
        policyType: Array.isArray(policyType) ? policyType : (requestedPolicyType ?? policyType),
      }),
    [fetchPolicyTemplateGroupList, policyType],
  )

  useEffect(() => {
    if (fetchOnMount) {
      requestProviderPolicyTemplatesList()
    }
  }, [fetchOnMount, requestProviderPolicyTemplatesList])

  useEffect(() => {
    if (fetchOnMount) {
      fetchProviderPolicyTemplateGroupList()
    }
  }, [fetchOnMount, fetchProviderPolicyTemplateGroupList])

  const provided = useMemo(
    () => ({
      // template
      loadingPolicyTemplates,
      policyTemplates,
      policyTemplatesError,
      requestPolicyTemplatesList: requestProviderPolicyTemplatesList,
      deletePolicyTemplate,
      createPolicyTemplate,
      updatePolicyTemplate,
      clearPolicyTemplateError,
      // group
      loadingPolicyTemplateGroup,
      errorPolicyTemplateGroup,
      policyTemplateGroups,
      createPolicyTemplateGroup,
      fetchPolicyTemplateGroupList: fetchProviderPolicyTemplateGroupList,
      deletePolicyTemplateGroup,
      updatePolicyTemplateGroup,
      clearPolicyTemplateGroupError,
    }),
    [
      loadingPolicyTemplates,
      policyTemplates,
      policyTemplatesError,
      requestProviderPolicyTemplatesList,
      deletePolicyTemplate,
      createPolicyTemplate,
      updatePolicyTemplate,
      clearPolicyTemplateError,
      loadingPolicyTemplateGroup,
      errorPolicyTemplateGroup,
      policyTemplateGroups,
      createPolicyTemplateGroup,
      fetchProviderPolicyTemplateGroupList,
      deletePolicyTemplateGroup,
      updatePolicyTemplateGroup,
      clearPolicyTemplateGroupError,
    ],
  )

  return <PolicyTemplatesContext.Provider value={provided}>{children}</PolicyTemplatesContext.Provider>
}
